import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSite } from "@/context/SiteDataContext";
import Seo from "@/components/Seo";
import PageHero from "@/components/common/PageHero";
import RichContent from "@/components/common/RichContent";
import { Skeleton } from "@/components/ui/skeleton";
import { legalService } from "@/services/contentServices";

/**
 * Privacy Policy and Terms of Use.
 *
 * The text is held in the `legalPages` collection and edited in the CMS under
 * Content → Legal pages, so the client can revise their own legal wording
 * without a deploy. These are documents that get amended by lawyers and by
 * regulation, not by developers.
 *
 * Nothing is hardcoded here: an empty state says so plainly rather than
 * showing stale placeholder terms, which would be worse than showing none.
 */
function LegalPage({ slug, fallbackTitle }) {
  const SITE = useSite();
  const [page, setPage] = useState(undefined);

  useEffect(() => {
    let live = true;
    setPage(undefined);
    legalService.getLegalPage(slug).then((p) => {
      if (live) setPage(p);
    });
    return () => {
      live = false;
    };
  }, [slug]);

  const title = page?.title || fallbackTitle;

  return (
    <>
      {/* Domain comes from site settings, not hardcoded — otherwise these two
          pages silently keep pointing at the old address after a domain move. */}
      <Seo
        routeKey={slug}
        title={title}
        description={`${title} — ${SITE.company || "Athena Scientific"}`}
        canonical={`https://${SITE.website}/${slug}`}
      />
      <PageHero eyebrow="Legal" title={title} crumbs={[{ label: title }]} />

      <section className="container-x py-16 md:py-24 max-w-3xl">
        {page === undefined && (
          <div className="space-y-4" data-testid="legal-loading">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className={i % 4 === 0 ? "h-7 w-2/5" : "h-4 w-full"} />
            ))}
          </div>
        )}

        {page === null && (
          <p className="text-greyish leading-relaxed" data-testid="legal-empty">
            This policy is being updated. Please{" "}
            <Link to="/contact" className="text-aqua font-semibold hover:underline">
              contact us
            </Link>{" "}
            for a current copy.
          </p>
        )}

        {page && (
          <>
            {page.lastUpdated && (
              <p className="text-sm text-greyish mb-8" data-testid="legal-updated">
                Last updated: {page.lastUpdated}
              </p>
            )}
            <RichContent blocks={page.content} />
            <p className="mt-10 pt-6 border-t border-border text-sm text-greyish">
              Have a question?{" "}
              <Link to="/contact" className="text-aqua font-semibold hover:underline">
                Contact us
              </Link>
              .
            </p>
          </>
        )}
      </section>
    </>
  );
}

export const Privacy = () => <LegalPage slug="privacy" fallbackTitle="Privacy Policy" />;
export const Terms = () => <LegalPage slug="terms" fallbackTitle="Terms of Use" />;
